import 'react-range-slider-input/dist/style.css';
import {
  CharacterModel,
  StoryCharacterAgeUnitEnum,
  StoryCharacterEnum
} from '../../../../interfaces/character.interface';
import { setStoryData } from '../../../../store/storySlice';
import { AppDispatch, RootState } from '../../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import CharacterForm from './CharacterDescriptionInputsForm';
import { CharacterDescriptionInputsProps } from '../../models/CharacterDescriptionInputsProps.interface';

const SELECTOR_FIELDS = new Set(['characterType', 'gender', 'ageUnit']);

const CharacterDescriptionInputs = ({
                                      setCharacterOutlineByUser,
                                      characterOutlineByUser,
                                      handleImageChange,
                                      setPreviewImage,
                                      previewImage,
                                      selectedImage,
                                      continueWithoutImage,
                                      errors,
                                      setErrors,
                                      validateField,
                                      handleDeleteImage,
                                      currentPortrait,
                                      setCurrentPortrait,
                                      setShouldCoverPortrait = null,
                                      onFieldDirty,
                                      onBlurSave,
                                    }: CharacterDescriptionInputsProps) => {
  const storyData = useSelector((state: RootState) => state.story.storyData);
  const dispatch: AppDispatch = useDispatch();

  // Local outline is the source of truth during the editing session.
  // - Initial draft load seeds it via getDraftDetails.
  // - Edits update it directly via handleInputChange.
  // - Autosave mirrors it to bookData on every blur/selector/submit/leave.
  // Previously a useEffect here re-seeded local from bookData/storyData on
  // mount (and on remount triggered by the loading-screen toggle), which
  // racing a fire-and-forget autosave caused the "description swap" bug:
  // the listener fires the pre-flight bookData clear before the autosave
  // write lands, so bookData briefly holds the previous outline; the
  // remount-time rehydration then overwrote the user's just-submitted
  // text with that stale value. Reading from bookData here is no longer
  // needed.

  const handleInputChange = (e: any) => {
    let { name, value } = e.target;
    // Switching characterType clears fields that don't apply to the new
    // type, but MUST preserve characterType itself (and `mics`, which is
    // shared across all three types). Previously the non-human branch
    // replaced the entire outline with a 3-key object that dropped
    // `characterType`, then a stale-storyData useEffect downstream
    // reset the dropdown back to "human" on the next render — making
    // the form revert after any switch+error round-trip.
    if (name === 'characterType') {
      if (value === StoryCharacterEnum.human) {
        setCharacterOutlineByUser((prevState: CharacterModel) => ({
          ...prevState,
          characterType: StoryCharacterEnum.human,
          ageUnit: prevState?.ageUnit || StoryCharacterAgeUnitEnum.year,
          animalType: undefined,
          character: undefined,
        }));

        dispatch(setStoryData({
          ...storyData,
          characterType: StoryCharacterEnum.human,
          ageUnit: storyData?.ageUnit || StoryCharacterAgeUnitEnum.year,
          animalType: undefined,
          character: undefined,
        }));
      } else {
        setCharacterOutlineByUser((prevState: CharacterModel) => ({
          ...prevState,
          characterType: value,
          age: undefined,
          gender: undefined,
        }));

        dispatch(setStoryData({
          ...storyData,
          characterType: value,
          age: '',
          gender: '',
        }));
      }
      validateField(name, value);
      onFieldDirty?.();
      if (SELECTOR_FIELDS.has(name)) {
        onBlurSave?.();
      }
      return;
    }

    value = name === 'age' ? parseInt(value) : value;
    if (name === 'age' && value > 0 && value <= 100) {
      setCharacterOutlineByUser((prevState: CharacterModel) => ({
        ...prevState,
        [name]: value
      }));

      dispatch(setStoryData({
        ...storyData,
        ['ageUnit']: StoryCharacterAgeUnitEnum.year,
        ['characterType']: StoryCharacterEnum.human
      }));

      dispatch(setStoryData({
        ...storyData,
        [name]: value
      }));
    } else {
      setCharacterOutlineByUser((prevState: any) => ({
        ...prevState,
        [name]: value
      }));

      dispatch(setStoryData({
        ...storyData,
        [name]: value
      }));
    }
    validateField(name, value);

    onFieldDirty?.();

    if (SELECTOR_FIELDS.has(name)) {
      onBlurSave?.();
    }
  };

  return (
    <CharacterForm
      handleImageChange={handleImageChange}
      characterOutlineByUser={characterOutlineByUser}
      errors={errors}
      handleInputChange={handleInputChange}
      storyData={storyData}
      previewImage={previewImage}
      setPreviewImage={setPreviewImage}
      selectedImage={selectedImage}
      currentPortrait={currentPortrait}
      setShouldCoverPortrait={setShouldCoverPortrait}
      continueWithoutImage={continueWithoutImage}
      onBlurSave={onBlurSave}
    />
  );
};

export default CharacterDescriptionInputs;
